import { useAuth } from "@clerk/clerk-react";
import { useCallback, useMemo } from "react";

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

async function rawFetch<T>(
  path: string,
  init: RequestInit = {},
  token: string | null,
): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(path, { ...init, headers });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, data?.error ?? res.statusText);
  }
  return data as T;
}

export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  return rawFetch<T>(path, { method: "POST", body: JSON.stringify(body) }, null);
}

export async function publicGet<T>(path: string): Promise<T> {
  return rawFetch<T>(path, { method: "GET" }, null);
}

export function useApi() {
  const { getToken } = useAuth();

  const get = useCallback(
    async <T>(path: string): Promise<T> => {
      const token = await getToken();
      return rawFetch<T>(path, { method: "GET" }, token);
    },
    [getToken],
  );

  const post = useCallback(
    async <T>(path: string, body: unknown): Promise<T> => {
      const token = await getToken();
      return rawFetch<T>(
        path,
        { method: "POST", body: JSON.stringify(body) },
        token,
      );
    },
    [getToken],
  );

  const patch = useCallback(
    async <T>(path: string, body: unknown): Promise<T> => {
      const token = await getToken();
      return rawFetch<T>(
        path,
        { method: "PATCH", body: JSON.stringify(body) },
        token,
      );
    },
    [getToken],
  );

  return useMemo(() => ({ get, post, patch }), [get, post, patch]);
}
